import {
  Component,
  Inject,
  PLATFORM_ID,
  Input,
  OnChanges,
  SimpleChanges,
  OnInit
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SpinService } from '../../../../services/spinservice';
import confetti from 'canvas-confetti';
import { CartService } from '../../../../services/cart';
import { OrdersService } from '../../../../services/orders';

@Component({
  selector: 'app-luckywheel',
  templateUrl: './luckywheel.component.html',
  styleUrls: ['./luckywheel.component.scss']
})
export class LuckywheelComponent implements OnChanges, OnInit {

  @Input() userId!: any;

  isAr = false;
  spinning = false;
  rotation = 0;
  resultText = '';
  closing = false;
  show = true;
  rewardProduct: any = null;
  previewProducts: any[] = [];

  private isBrowser: boolean;

  // 🔊 Sounds
  tickAudios: HTMLAudioElement[] = [];
  tickIndex = 0;
  winAudio!: HTMLAudioElement;

  // 🛞 Wheel config
  sliceDeg = 60;
  lastTickSlice = -1;

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private spinService: SpinService,
    private _orderService: OrdersService,
    private cartService: CartService
  ) {
    this.isBrowser = isPlatformBrowser(platformId);

    if (this.isBrowser) {
      for (let i = 0; i < 6; i++) {
        const audio = new Audio('/assets/sounds/wheel-tick.mp3');
        audio.volume = 0.5;
        this.tickAudios.push(audio);
      }

      this.winAudio = new Audio('/assets/sounds/win.mp3');
      this.winAudio.volume = 0.9;
    }
  }

  // ✅ FIXED (مش OnInit)
  ngOnInit(): void {
    if (this.isBrowser) {
      this.isAr = localStorage.getItem('language') === 'ar';
    }
  }

  // ==========================
  // Listen for userId
  // ==========================
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userId']) {
      this.loadPreviewProducts();
    }
  }

  // ==========================
  // Load preview products
  // ==========================
  loadPreviewProducts(): void {
    this.spinService.getActive(this.userId ?? null)
      .subscribe(res => {
        if (res.preview_products) {
          this.previewProducts = res.preview_products;
        }
      });
  }

  // ==========================
  // Spin (ANTI-CHEAT VERSION)
  // ==========================
  spin(): void {

    if (this.spinning || this.previewProducts.length < 6) return;

    this.spinning = true;
    this.resultText = '';

    const spinDuration = 8500;

    // ⛔ نستنى النتيجة الأول
    this.spinService.spin(this.userId ?? null)
      .subscribe({
        next: (res) => {

          if (!res.product) {
            this.spinning = false;
            return;
          }

          const winningProductId = res.product.id;

          const sliceIndex = this.previewProducts.findIndex(
            p => p.id === winningProductId
          );

          if (sliceIndex === -1) {
            console.error('Winning product not found');
            this.spinning = false;
            return;
          }

          // 👇 نحسب زاوية الوقوف صح تحت المؤشر
          const finalAngle =
            360 - (sliceIndex * this.sliceDeg) - (this.sliceDeg / 2);

          const extraSpins = 8 + Math.floor(Math.random() * 5);

          const targetRotation =
            this.rotation + (extraSpins * 360) + finalAngle;

          // 👇 يبدأ اللف بعد ما عرفنا النتيجة
          this.animateSpin(targetRotation, spinDuration);

          setTimeout(() => {

            this.spinning = false;
            this.rewardProduct = res.product;

            this.resultText = this.isAr
              ? `مبروك! ${res.product.name}`
              : `Congratulations! ${res.product.name}`;

            this.addFreeProductToCart(res.product.id);

            this.playWinSound();
            this.fireConfetti();

          }, spinDuration);
        },

        error: (err) => {

          this.spinning = false;

          this.resultText =
            err.status === 403
              ? this.isAr
                ? '❌ لا يمكنك المحاولة مرة أخرى'
                : '❌ You cannot try again'
              : this.isAr
                ? '⚠️ حدث خطأ، حاول لاحقًا'
                : '⚠️ An error occurred, please try again later';
        }
      });
  }



  // ==========================
  // Animation
  // ==========================
  animateSpin(targetRotation: number, duration: number) {

    const start = performance.now();
    const initialRotation = this.rotation;

    this.lastTickSlice =
      Math.floor((initialRotation % 360) / this.sliceDeg);

    const animate = (now: number) => {

      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);

      this.rotation =
        initialRotation +
        (targetRotation - initialRotation) * easeOut;

      const currentSlice =
        Math.floor((this.rotation % 360) / this.sliceDeg);

      if (currentSlice !== this.lastTickSlice) {
        this.lastTickSlice = currentSlice;
        this.playTick();
      }

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }

  // ==========================
  // Sounds
  // ==========================
  playTick() {
    if (!this.isBrowser || !this.tickAudios.length) return;

    const audio = this.tickAudios[this.tickIndex];
    audio.currentTime = 0;
    audio.play().catch(() => { });

    this.tickIndex =
      (this.tickIndex + 1) % this.tickAudios.length;
  }

  playWinSound() {
    if (!this.isBrowser) return;
    this.winAudio.currentTime = 0;
    this.winAudio.play().catch(() => { });
  }

  // ==========================
  // Confetti
  // ==========================
  fireConfetti(): void {

    confetti({
      particleCount: 200,
      spread: 100,
      origin: { x: 0.5, y: 0.45 }
    });

    setTimeout(() => {
      confetti({
        particleCount: 120,
        spread: 140,
        origin: { x: 0.5, y: 0.45 }
      });
    }, 400);
  }

  // ==========================
  // Cart
  // ==========================
  addFreeProductToCart(productId: number): void {

    this._orderService.getByIds([productId])
      .subscribe((data: any) => {

        const product = { ...(data as any[])[0] };
        product.is_free_reward = true;

        this.cartService.addToCart(product);

        setTimeout(() => {
          this.closing = true;
          this.show = false;
        }, 5000);
      });
  }

  // ==========================
  // Helpers
  // ==========================
  transformImageUrl(url: string | null): string {
    if (!url) return '';

    // لو already صح
    if (url.includes('/storage/app/public/')) {
      return url;
    }

    // نحول من /storage/images إلى /storage/app/public/images
    return url.replace(
      '/storage/images/',
      '/storage/app/public/images/'
    );
  }

}
